import React, { useState, useRef, useEffect } from "react";
import {
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
} from "@material-ui/core";

import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import EventIcon from "@material-ui/icons/Event";
import TuneOutlinedIcon from "@material-ui/icons/TuneOutlined";
import PhoneInTalkOutlinedIcon from "@material-ui/icons/PhoneInTalkOutlined";
import ListAltOutlinedIcon from "@material-ui/icons/ListAltOutlined";
import SwapVertIcon from "@material-ui/icons/SwapVert";
import MenuIcon from "@material-ui/icons/Menu";
import CheckIcon from "@material-ui/icons/Check";
import styles from "./SuperAdminLayout.module.css";

const SECTIONS = [
  {
    caption: "Overview",
    items: [{ key: "overview", label: "Overview", icon: DashboardOutlinedIcon, badge: styles.navIconBadgeOrange }],
  },
  {
    caption: "Management",
    items: [{ key: "users", label: "Users", icon: PeopleAltOutlinedIcon, badge: styles.navIconBadgePurple }],
  },
  {
    caption: "System",
    items: [
      { key: "subscription", label: "Subscr.", icon: EventIcon, badge: styles.navIconBadgeTeal },
      { key: "support", label: "Support", icon: PhoneInTalkOutlinedIcon, badge: styles.navIconBadgeGreen },
      { key: "settings", label: "Settings", icon: TuneOutlinedIcon, badge: styles.navIconBadgeBlue },
      { key: "activityLogs", label: "Logs", icon: ListAltOutlinedIcon, badge: styles.navIconBadgeOrange },
      { key: "stockLedger", label: "Ledger", icon: SwapVertIcon, badge: styles.navIconBadgeTeal },
    ],
  },
];

// Flatten all items for the bottom tab bar (mobile shows max 5 visible + overflow)
const ALL_ITEMS = SECTIONS.flatMap((s) => s.items);
const VISIBLE_TAB_COUNT = 5; // max visible tabs before "More" appears

// Badge color map for bottom-tab icon colour
const BADGE_COLOR_MAP = {
  [styles.navIconBadgeOrange]: "#f97316",
  [styles.navIconBadgePurple]: "#8b5cf6",
  [styles.navIconBadgeTeal]: "#10b981",
  [styles.navIconBadgeGreen]: "#25d366",
  [styles.navIconBadgeBlue]: "#3b82f6",
};

const SuperAdminSidebar = ({ active, onSelect }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const activeItemRef = useRef(null);
  const isMobile = useMediaQuery("(max-width:600px)");

  useEffect(() => {
    if (activeItemRef.current && !isMobile) {
      activeItemRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [active, isMobile]);

  // ── Mobile: fixed bottom tab bar ────────────────────────────────────
  if (isMobile) {
    const visibleItems = ALL_ITEMS.slice(0, VISIBLE_TAB_COUNT);
    const overflowItems = ALL_ITEMS.slice(VISIBLE_TAB_COUNT);
    const activeIsOverflow = overflowItems.some((i) => i.key === active);

    return (
      <>
        {/* Zero-height placeholder so .shell flex layout still works */}
        <nav className={styles.sidebar} aria-hidden="true" />

        {/* Fixed bottom tab bar */}
        <div className={styles.mobileBottomNav} role="navigation" aria-label="Main navigation">
          {visibleItems.map(({ key, label, icon: Icon, badge }) => {
            const isActive = active === key;
            const iconColor = BADGE_COLOR_MAP[badge] || "#ea580c";
            return (
              <button
                key={key}
                type="button"
                className={`${styles.mobileTabItem} ${isActive ? styles.mobileTabItemActive : ""}`}
                onClick={() => onSelect(key)}
                aria-label={label}
                aria-current={isActive ? "page" : undefined}
              >
                <span
                  className={styles.mobileTabIcon}
                  style={{ color: isActive ? iconColor : "#94a3b8" }}
                >
                  <Icon style={{ fontSize: 20 }} />
                </span>
                <span className={styles.mobileTabLabel}>{label}</span>
              </button>
            );
          })}

          {/* Overflow "More" tab – only shown when there are > 5 items */}
          {overflowItems.length > 0 && (
            <>
              <button
                type="button"
                className={`${styles.mobileTabItem} ${activeIsOverflow ? styles.mobileTabItemActive : ""}`}
                onClick={(e) => setMobileMenuAnchor(e.currentTarget)}
                aria-label="More navigation options"
              >
                <span className={styles.mobileTabIcon} style={{ color: activeIsOverflow ? "#ea580c" : "#94a3b8" }}>
                  <MenuIcon style={{ fontSize: 20 }} />
                </span>
                <span className={styles.mobileTabLabel}>
                  {activeIsOverflow ? ALL_ITEMS.find((i) => i.key === active)?.label : "More"}
                </span>
              </button>

              <Menu
                anchorEl={mobileMenuAnchor}
                open={Boolean(mobileMenuAnchor)}
                onClose={() => setMobileMenuAnchor(null)}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "bottom", horizontal: "right" }}
                classes={{ paper: styles.quickNavMenuPaper }}
                style={{ zIndex: 10010 }}
              >
                <div className={styles.quickNavHeader}>More Sections</div>
                {overflowItems.map(({ key, label, icon: Icon, badge }) => {
                  const isItemActive = active === key;
                  return (
                    <MenuItem
                      key={key}
                      selected={isItemActive}
                      className={`${styles.quickNavItem} ${isItemActive ? styles.quickNavItemActive : ""}`}
                      onClick={() => {
                        setMobileMenuAnchor(null);
                        onSelect(key);
                      }}
                    >
                      <ListItemIcon style={{ minWidth: 32 }}>
                        <span className={`${styles.navIconBadge} ${badge}`} style={{ width: 24, height: 24 }}>
                          <Icon style={{ fontSize: 14 }} />
                        </span>
                      </ListItemIcon>
                      <ListItemText
                        primary={label}
                        primaryTypographyProps={{
                          style: {
                            fontSize: "0.84rem",
                            fontWeight: isItemActive ? 700 : 500,
                            color: isItemActive ? "#ea580c" : "#1e293b",
                          },
                        }}
                      />
                      {isItemActive && <CheckIcon style={{ fontSize: 16, color: "#ea580c" }} />}
                    </MenuItem>
                  );
                })}
              </Menu>
            </>
          )}
        </div>
      </>
    );
  }

  // ── Desktop / tablet: vertical sidebar ──────────────────────────────
  return (
    <nav className={`${styles.sidebar} ${collapsed ? styles.sidebarCollapsed : ""}`}>
      <div className={styles.sidebarNav}>
        {SECTIONS.map((section) => (
          <div key={section.caption} className={styles.navSectionGroup}>
            {!collapsed && <div className={styles.navCaption}>{section.caption}</div>}
            {section.items.map(({ key, label, icon: Icon, badge }) => {
              const isActive = active === key;
              const item = (
                <div
                  key={key}
                  ref={isActive ? activeItemRef : null}
                  className={`${styles.navItem} ${isActive ? styles.navItemActive : ""}`}
                  onClick={() => onSelect(key)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => (e.key === "Enter" ? onSelect(key) : null)}
                >
                  <span className={`${styles.navIconBadge} ${badge}`}>
                    <Icon fontSize="small" />
                  </span>
                  {!collapsed && <span className={styles.navLabel}>{label}</span>}
                </div>
              );
              return collapsed ? (
                <Tooltip key={key} title={label} arrow placement="right">
                  {item}
                </Tooltip>
              ) : (
                item
              );
            })}
          </div>
        ))}
      </div>

      <Tooltip title={collapsed ? "Expand sidebar" : "Collapse sidebar"} arrow placement="right">
        <div
          className={`${styles.collapseToggleRow} ${collapsed ? styles.collapseToggleFlipped : ""}`}
          onClick={() => setCollapsed((c) => !c)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === "Enter" ? setCollapsed((c) => !c) : null)}
        >
          <ChevronLeftIcon fontSize="small" />
          {!collapsed && <span>Collapse</span>}
        </div>
      </Tooltip>
    </nav>
  );
};

export default SuperAdminSidebar;
